import { useMilestones } from "@/hooks/use-mock-data";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Plus, Calendar, Target, TrendingUp, Rocket, BarChart3 } from "lucide-react";

const statusColors: Record<string, string> = {
  not_started: "#555555",
  in_progress: "#4B75F2",
  at_risk: "#EBF216",
  achieved: "#44F2D2",
};

const metricIcons: Record<string, typeof Target> = {
  revenue: TrendingUp,
  clients: BarChart3,
  ventures_launched: Rocket,
  opportunities_evaluated: Target,
  custom: Target,
};

export default function MilestonesPage() {
  const { data: milestones } = useMilestones();

  return (
    <div className="animate-fade-in max-w-4xl mx-auto px-6 py-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-brand text-4xl tracking-wide">MILESTONE ROADMAP</h1>
        <Button className="bg-electric-blue hover:bg-electric-blue/90 text-white text-xs uppercase tracking-wider">
          <Plus className="w-4 h-4 mr-1" /> Add Milestone
        </Button>
      </div>

      {/* Vertical timeline */}
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-px bg-[rgba(255,255,255,0.06)]" />

        <div className="space-y-6">
          {milestones.map((ms) => {
            const pct = Math.min((ms.currentValue / ms.targetValue) * 100, 100);
            const color = statusColors[ms.status] ?? "#555555";
            const Icon = metricIcons[ms.metricType] ?? Target;

            return (
              <div key={ms.id} className="relative pl-14">
                {/* Timeline dot */}
                <div
                  className="absolute left-4 top-4 w-4 h-4 rounded-full border-2 bg-black"
                  style={{ borderColor: color, boxShadow: `0 0 8px ${color}30` }}
                />

                <div className="glass-card rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4" style={{ color }} />
                      <h3 className="text-foreground font-medium">{ms.title}</h3>
                    </div>
                    <span
                      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded font-brand shrink-0"
                      style={{ color, backgroundColor: `${color}12` }}
                    >
                      {ms.status.replace(/_/g, " ")}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">{ms.description}</p>
                  <Progress value={pct} className="h-2 mb-2" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="font-mono">
                      {ms.metricType === "revenue"
                        ? `$${ms.currentValue.toLocaleString()} / $${ms.targetValue.toLocaleString()}`
                        : `${ms.currentValue} / ${ms.targetValue}`}
                      <span className="ml-1">({pct.toFixed(0)}%)</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(ms.targetDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
